export interface StrengthResult {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
  pct: number;
  crackTime: string;
  warning: string;
  suggestions: string[];
}

const LEVELS = [
  { label: "Very weak", color: "bg-red-600", pct: 15 },
  { label: "Weak", color: "bg-red-500", pct: 30 },
  { label: "Fair", color: "bg-yellow-500", pct: 55 },
  { label: "Strong", color: "bg-green-500", pct: 80 },
  { label: "Very strong", color: "bg-green-600", pct: 100 },
] as const;

/** Rating band from the offline slow-hash crack time, so the label never contradicts the estimate shown beside it. */
export function bandFromSeconds(sec: number): 0 | 1 | 2 | 3 | 4 {
  if (sec < 60) return 0;
  if (sec < 86400) return 1;
  if (sec < 31536000) return 2;
  if (sec < 31536000 * 100) return 3;
  return 4;
}

let ready: Promise<{ check: (p: string) => ReturnType<import("@zxcvbn-ts/core").ZxcvbnFactory["check"]> }> | null = null;

function load() {
  ready ??= (async () => {
    const [core, common, en] = await Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
      import("@zxcvbn-ts/language-en"),
    ]);
    const z = new core.ZxcvbnFactory({
      translations: en.translations,
      graphs: common.adjacencyGraphs,
      dictionary: { ...common.dictionary, ...en.dictionary },
    });
    return { check: (p: string) => z.check(p) };
  })();
  return ready;
}

/** Pattern-based estimate (zxcvbn): dictionary words, keyboard walks, repeats, years, l33t. Runs locally. */
export async function checkPassword(password: string): Promise<StrengthResult> {
  const z = await load();
  // zxcvbn is slow on very long input; the first 100 chars is plenty.
  const r = await z.check(password.slice(0, 100));
  const seconds = r.guesses / 1e4; // same rate the displayed estimate uses (offline, slow hash)
  const score = Math.min(r.score, Number.isFinite(seconds) ? bandFromSeconds(seconds) : 4) as 0 | 1 | 2 | 3 | 4;
  return {
    score,
    ...LEVELS[score],
    crackTime: r.crackTimes.offlineSlowHashingXPerSecond.display,
    warning: r.feedback.warning ?? "",
    suggestions: r.feedback.suggestions,
  };
}
